import { useState, useEffect, useCallback, useRef } from 'react'
import { io, Socket } from 'socket.io-client'
import axios from 'axios'
import { toast } from 'sonner'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useUserStore } from '@/store/userStore'
import { Message } from '@/types/chat'

type UserRole = 'Teacher' | 'Student' | 'Parent'

interface User {
  id: string
  firstname: string
  lastname: string
  role: UserRole
  avatarUrl?: string
}

interface Conversation {
  id: string
  name?: string
  participants: User[]
  messages: Message[]
  createdAt: string
  updatedAt: string
  createdBy: string
  unreadCount?: number
  isGroupChat?: boolean
}

// Define API URL with fallback options
const API_URL = import.meta.env.VITE_API_URL

// Add a utility function to check server availability
const checkServerAvailability = async () => {
  try {
    // Use a simple endpoint that doesn't require authentication
    const response = await axios.get(`${API_URL}/health`, { timeout: 5000 })
    return response.status === 200
  } catch (error) {
    // console.error('Server availability check failed:', error)
    return false
  }
}

// API functions for React Query (HTTP-only cookies sent automatically)
const api = {
  getConversations: async () => {
    const response = await axios.get(`${API_URL}/chat/conversations`, {
      withCredentials: true, // Send cookies automatically
    })
    return response.data
  },

  getConversation: async ({
    conversationId,
  }: {
    conversationId: string
  }) => {
    const response = await axios.get(
      `${API_URL}/chat/conversations/${conversationId}`,
      {
        withCredentials: true, // Send cookies automatically
      }
    )
    return response.data
  },

  sendMessage: async ({
    content,
    conversationId,
  }: {
    content: string
    conversationId: string
  }) => {
    const response = await axios.post(
      `${API_URL}/chat/messages`,
      { content, conversationId }, // Only send fields expected by SendMessageDto
      { withCredentials: true } // Send cookies automatically
    )
    return response.data
  },

  searchUsers: async ({
    term,
    userRole,
  }: {
    term: string
    userRole: UserRole
  }) => {
    let url = ''
    if (userRole === 'Teacher') {
      url = `${API_URL}/student/search-by-name?term=${encodeURIComponent(term)}`
    } else if (userRole === 'Parent' || userRole === 'Student') {
      url = `${API_URL}/teacher/search-by-name?term=${encodeURIComponent(term)}`
    } else {
      // Fallback for other roles or if logic needs to be extended
      url = `${API_URL}/users/search?term=${encodeURIComponent(term)}&userRole=${userRole}`
    }

    const response = await axios.get(url, {
      withCredentials: true, // Send cookies automatically
      timeout: 10000,
    })
    return response.data
  },

  // Function to search users (for adding participants to groups)
  searchUsersSimple: async (query: string) => {
    if (!query || query.trim().length < 2) {
      return []
    }
    try {
      const response = await axios.get(`${API_URL}/users/search`, {
        params: { term: query, userRole: 'Teacher' }, // Default to Teacher if not specified, or maybe generic
        withCredentials: true,
      })
      return response.data
    } catch (error) {
      // console.error('Error searching users:', error)
      return []
    }
  },

  // Function to search students by name
  searchStudents: async (term: string) => {
    if (!term || term.trim().length < 2) {
      return []
    }
    try {
      const response = await axios.get(`${API_URL}/student/search-by-name`, {
        params: { term },
        withCredentials: true,
      })
      return response.data
    } catch (error) {
      // console.error('Error searching students:', error)
      return []
    }
  },

  markMessagesAsRead: async ({
    conversationId,
  }: {
    conversationId: string
  }) => {
    await axios.patch(
      `${API_URL}/chat/conversations/${conversationId}/read`,
      {},
      { withCredentials: true } // Send cookies automatically
    )
  },

  createConversation: async ({
    participantIds,
    isGroupChat,
    name,
  }: {
    participantIds: string[]
    isGroupChat: boolean
    name?: string
  }) => {
    const response = await axios.post(
      `${API_URL}/chat/create-conversation`,
      { participantIds, isGroupChat, name },
      { withCredentials: true } // Send cookies automatically
    )
    return response.data
  },

  createTeacherStudentChat: async ({
    studentId,
    initialMessage,
  }: {
    studentId: string
    initialMessage: string
  }) => {
    const response = await axios.post(
      `${API_URL}/chat/teacher-student`,
      { studentId, initialMessage },
      { withCredentials: true } // Send cookies automatically
    )
    return response.data
  },
}

export const useChat = () => {
  // Query client for React Query
  const queryClient = useQueryClient()

  // User state
  const [currentUser, setCurrentUser] = useState<User | null>(null)

  // Socket state
  const [socket, setSocket] = useState<Socket | null>(null)

  // UI state
  const [selectedConversationId, setSelectedConversationId] = useState<
    string | null
  >(null)
  const [message, setMessage] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  // Search functionality
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [isSearching, setIsSearching] = useState<boolean>(false)
  const [isSearchLoading, setIsSearchLoading] = useState<boolean>(false)
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Conversation filtering
  const [conversationFilter, setConversationFilter] = useState<string>('')

  // Add messagesEndRef for scrolling to bottom of messages
  const messagesEndRef = useRef<HTMLDivElement>(null)

  // Add a state to track server availability
  const [isServerAvailable, setIsServerAvailable] = useState<boolean | null>(
    null
  )

  // Check server availability on mount
  useEffect(() => {
    const checkServer = async () => {
      setIsLoading(true)
      const isAvailable = await checkServerAvailability()
      setIsServerAvailable(isAvailable)
      if (!isAvailable) {
        setIsLoading(false)
        toast.error(
          'Unable to connect to the server. Please check your connection and try again.'
        )
      }
    }

    checkServer()
  }, [])

  // Subscribe to userStore to get user data (reacts to changes)
  const userStoreUser = useUserStore((state) => state.user)
  const userStoreIsLoading = useUserStore((state) => state.isLoading)
  const userStoreIsAuthenticated = useUserStore((state) => state.isAuthenticated)

  // Initialize user from userStore (fetched from API using HTTP-only cookies)
  useEffect(() => {
    // Wait for userStore to finish loading
    if (userStoreIsLoading) {
      return // Still loading, wait
    }

    const user = userStoreUser

    if (user && user.id && user.role && user.firstname && user.lastname) {
      const chatUser: User = {
        id: user.id,
        firstname: user.firstname,
        lastname: user.lastname,
        role: user.role as UserRole,
      }
      setCurrentUser(chatUser)
  // console.logUser data loaded from userStore')
    } else if (userStoreIsAuthenticated) {
      // User is authenticated but data might be loading, try to verify
      const userStore = useUserStore.getState()
      userStore.verifySession().then((verified) => {
        const updatedUser = useUserStore.getState().user
        if (verified && updatedUser) {
          const chatUser: User = {
            id: updatedUser.id,
            firstname: updatedUser.firstname,
            lastname: updatedUser.lastname,
            role: updatedUser.role as UserRole,
          }
          setCurrentUser(chatUser)
      // console.logUser data loaded from API after verification')
        } else {
          setIsLoading(false)
          console.warn('User data not found in userStore')
          toast.error('User data not found. Please log in again.')
        }
      })
    } else {
      // Not authenticated and not loading - user needs to login
      setIsLoading(false)
      console.warn('User not authenticated')
    }
  }, [userStoreUser, userStoreIsLoading, userStoreIsAuthenticated])

  // Initialize socket connection with better error handling
  // Wait for userStore to be fully loaded and authenticated before connecting
  useEffect(() => {
    // Don't connect if:
    // 1. No current user
    // 2. Server not available
    // 3. UserStore still loading
    // 4. User not authenticated
    if (!currentUser || !isServerAvailable || userStoreIsLoading || !userStoreIsAuthenticated) {
      // If no user or server is not available, we're not in a loading state
      if (!userStoreIsLoading) {
        setIsLoading(false)
      }
      return
    }

    // Add a small delay to ensure cookies are fully available
    // This helps prevent AUTH_REQUIRED errors on initial connection
    const connectTimeout = setTimeout(() => {
      setIsLoading(true)
  // console.logInitializing socket connection...')

      // Create socket connection with auth (HTTP-only cookies sent with withCredentials)
      const newSocket = io(API_URL, {
        auth: {
          userData: { userId: currentUser.id, role: currentUser.role },
        },
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
        transports: ['websocket', 'polling'], // Try websocket first, fallback to polling
        forceNew: false,
        timeout: 10000, // 10 second timeout for connection
        withCredentials: true, // Send HTTP-only cookies with WebSocket connection
      })

      // Add connection timeout
      const connectionTimeout = setTimeout(() => {
        if (newSocket.connected) return

        // console.error('Socket connection timeout')
        setIsLoading(false)
        toast.error(
          'Connection timeout. Please check your internet connection and try again.'
        )

        // Clean up socket
        newSocket.removeAllListeners()
        newSocket.disconnect()
      }, 15000) // 15 seconds timeout

      // Socket event handlers
      newSocket.on('connect', () => {
    // console.logSocket connected successfully')
        clearTimeout(connectionTimeout)

        // Request conversations via socket for real-time updates
        newSocket.emit('getConversations')
        setIsLoading(false)

        // For students, request any pending conversations
        if (currentUser.role === 'Student') {
          newSocket.emit('getPendingConversations')
        }

        // For teachers, request any pending student conversations
        if (currentUser.role === 'Teacher') {
          newSocket.emit('getPendingStudentConversations')
        }
      })

      newSocket.on('connect_error', () => {
        // console.error('Socket connection error:', error)
        clearTimeout(connectionTimeout)
        setIsLoading(false)
        // Don't show toast for connection errors - socket will retry
      })

      newSocket.on('reconnect', () => {
    // console.logSocket reconnected')
        // Invalidate queries to reload data
        queryClient.invalidateQueries({ queryKey: ['conversations'] })

        // For students, request any pending conversations after reconnect
        if (currentUser.role === 'Student') {
          newSocket.emit('getPendingConversations')
        }

        // For teachers, request any pending student conversations after reconnect
        if (currentUser.role === 'Teacher') {
          newSocket.emit('getPendingStudentConversations')
        }
      })

      newSocket.on('error', (error: any) => {
        // console.error('Socket error:', error)
        // Suppress AUTH_REQUIRED errors - they're handled by the backend
        // The socket will retry and connect once cookies are properly sent
        if (error.code !== 'AUTH_REQUIRED') {
          // Only show non-auth errors
          if (!newSocket.connected) {
            // Only show error if socket is not connected
            toast.error(error.message || 'Connection error')
          }
        }
      })

      newSocket.on('disconnect', () => {
    // console.logSocket disconnected')
        // Don't set loading to false here as we might reconnect
      })

      setSocket(newSocket)

      // Cleanup function
      return () => {
        clearTimeout(connectionTimeout)
    // console.logCleaning up socket connection')
        newSocket.removeAllListeners()
        newSocket.disconnect()
      }
    }, 100) // Small delay to ensure cookies are ready

    return () => {
      clearTimeout(connectTimeout)
    }
  }, [
    currentUser,
    queryClient,
    isServerAvailable,
    selectedConversationId,
    userStoreIsLoading,
    userStoreIsAuthenticated,
  ])

  // Set up socket event listeners when socket is available
  useEffect(() => {
    if (!socket) return

    // Add listener for new conversations
    socket.on('newConversation', (conversation) => {
  // console.logNew conversation received:', conversation)

      // Update the conversations list
      queryClient.setQueryData(['conversations'], (oldData: any) => {
        if (!oldData) return [conversation]

        // Check if conversation already exists
        const exists = oldData.some(
          (c: Conversation) => c.id === conversation.id
        )
        if (exists) return oldData

        // Add the new conversation to the list and sort
        return [conversation, ...oldData].sort(
          (a: Conversation, b: Conversation) => {
            const timeA = new Date(a.updatedAt).getTime()
            const timeB = new Date(b.updatedAt).getTime()

            // Also check last message time as fallback or if newer
            const lastMsgTimeA = a.messages && a.messages.length > 0
              ? new Date(a.messages[a.messages.length - 1].createdAt).getTime()
              : 0
            const lastMsgTimeB = b.messages && b.messages.length > 0
              ? new Date(b.messages[b.messages.length - 1].createdAt).getTime()
              : 0

            const effectiveTimeA = Math.max(timeA, lastMsgTimeA)
            const effectiveTimeB = Math.max(timeB, lastMsgTimeB)

            return effectiveTimeB - effectiveTimeA
          }
        )
      })

      // Automatically select the new conversation for all users if no conversation is selected
      if (!selectedConversationId) {
        setSelectedConversationId(conversation.id)
      }
    })

    // GROUP CHAT EVENT LISTENERS

    // Listen for group created event
    socket.on('groupCreated', (data: any) => {
  // console.logGroup created:', data)
      const { conversation } = data

      // Update conversations list
      queryClient.setQueryData(['conversations'], (oldData: any) => {
        if (!oldData) return [conversation]

        const exists = oldData.some((c: Conversation) => c.id === conversation.id)
        if (exists) return oldData

        return [conversation, ...oldData].sort(
          (a: Conversation, b: Conversation) => {
            const timeA = new Date(a.updatedAt).getTime()
            const timeB = new Date(b.updatedAt).getTime()

            // Also check last message time as fallback or if newer
            const lastMsgTimeA = a.messages && a.messages.length > 0
              ? new Date(a.messages[a.messages.length - 1].createdAt).getTime()
              : 0
            const lastMsgTimeB = b.messages && b.messages.length > 0
              ? new Date(b.messages[b.messages.length - 1].createdAt).getTime()
              : 0

            const effectiveTimeA = Math.max(timeA, lastMsgTimeA)
            const effectiveTimeB = Math.max(timeB, lastMsgTimeB)

            return effectiveTimeB - effectiveTimeA
          }
        )
      })

      // Show success notification
      toast.success(`Group chat "${conversation.name}" created successfully`)

      // Select the new group chat
      if (!selectedConversationId) {
        setSelectedConversationId(conversation.id)
      }
    })

    // Listen for participant added event
    socket.on('participantAdded', (data: any) => {
  // console.logParticipant added:', data)
      const { conversationId, participant } = data

      // Update the conversation's participants
      queryClient.setQueryData(['conversation', conversationId], (oldData: any) => {
        if (!oldData) return oldData

        return {
          ...oldData,
          conversationParticipants: [
            ...(oldData.conversationParticipants || []),
            participant,
          ],
        }
      })

      // Show notification
      toast.info(
        `${participant.user.firstname} ${participant.user.lastname} joined the group`
      )

      // Invalidate conversations to refresh participant counts
      queryClient.invalidateQueries({ queryKey: ['conversations'] })
    })

    // Listen for participant removed event
    socket.on('participantRemoved', (data: any) => {
  // console.logParticipant removed:', data)
      const { conversationId, userId } = data

      // Update the conversation's participants
      queryClient.setQueryData(['conversation', conversationId], (oldData: any) => {
        if (!oldData) return oldData

        return {
          ...oldData,
          conversationParticipants: (oldData.conversationParticipants || []).filter(
            (p: any) => p.userId !== userId
          ),
        }
      })

      // Show notification
      toast.info('A participant was removed from the group')

      // Invalidate conversations to refresh participant counts
      queryClient.invalidateQueries({ queryKey: ['conversations'] })
    })

    // Listen for participant left event
    socket.on('participantLeft', (data: any) => {
  // console.logParticipant left:', data)
      const { conversationId, userId } = data

      // Update the conversation's participants
      queryClient.setQueryData(['conversation', conversationId], (oldData: any) => {
        if (!oldData) return oldData

        return {
          ...oldData,
          conversationParticipants: (oldData.conversationParticipants || []).map(
            (p: any) =>
              p.userId === userId ? { ...p, status: 'LEFT' } : p
          ),
        }
      })

      // Show notification if it's the current user
      if (userId === currentUser?.id) {
        toast.info('You left the group')
        // Remove from conversations list
        queryClient.setQueryData(['conversations'], (oldData: any) => {
          if (!oldData) return oldData
          return oldData.filter((c: Conversation) => c.id !== conversationId)
        })
        // Clear selection if this was the selected conversation
        if (selectedConversationId === conversationId) {
          setSelectedConversationId(null)
        }
      } else {
        toast.info('A participant left the group')
      }

      // Invalidate conversations to refresh participant counts
      queryClient.invalidateQueries({ queryKey: ['conversations'] })
    })

    // Listen for participant kicked event
    socket.on('participantKicked', (data: any) => {
  // console.logParticipant kicked:', data)
      const { conversationId, userId } = data

      // Update the conversation's participants
      queryClient.setQueryData(['conversation', conversationId], (oldData: any) => {
        if (!oldData) return oldData

        return {
          ...oldData,
          conversationParticipants: (oldData.conversationParticipants || []).map(
            (p: any) =>
              p.userId === userId ? { ...p, status: 'KICKED' } : p
          ),
        }
      })

      // Show notification if it's the current user
      if (userId === currentUser?.id) {
        toast.error('You were removed from the group')
        // Remove from conversations list
        queryClient.setQueryData(['conversations'], (oldData: any) => {
          if (!oldData) return oldData
          return oldData.filter((c: Conversation) => c.id !== conversationId)
        })
        // Clear selection if this was the selected conversation
        if (selectedConversationId === conversationId) {
          setSelectedConversationId(null)
        }
      } else {
        toast.info('A participant was removed from the group')
      }

      // Invalidate conversations to refresh participant counts
      queryClient.invalidateQueries({ queryKey: ['conversations'] })
    })

    // Listen for invite link generated event
    socket.on('inviteLinkGenerated', (data: any) => {
  // console.logInvite link generated:', data)
      const { inviteLink } = data

      // Show success notification with copy option
      toast.success('Invite link generated', {
        description: 'Click to copy the invite link',
        action: {
          label: 'Copy',
          onClick: () => {
            navigator.clipboard.writeText(inviteLink)
            toast.success('Invite link copied to clipboard')
          },
        },
      })
    })

    // Listen for group joined via invite event
    socket.on('groupJoinedViaInvite', (data: any) => {
  // console.logGroup joined via invite:', data)
      const { conversationId, user } = data

      // Update the conversation's participants
      queryClient.setQueryData(['conversation', conversationId], (oldData: any) => {
        if (!oldData) return oldData

        // Check if participant already exists
        const exists = (oldData.conversationParticipants || []).some(
          (p: any) => p.userId === user.id
        )
        if (exists) return oldData

        return {
          ...oldData,
          conversationParticipants: [
            ...(oldData.conversationParticipants || []),
            {
              userId: user.id,
              user: user,
              role: 'MEMBER',
              status: 'ACTIVE',
              joinedAt: new Date().toISOString(),
            },
          ],
        }
      })

      // Show notification
      toast.info(`${user.firstname} ${user.lastname} joined via invite link`)

      // Invalidate conversations to refresh participant counts
      queryClient.invalidateQueries({ queryKey: ['conversations'] })
    })

    // Listen for added to group event (current user was added to a group)
    socket.on('addedToGroup', (data: any) => {
  // console.logAdded to group:', data)
      const { conversationId } = data

      // Refresh conversations list to include the new group
      queryClient.invalidateQueries({ queryKey: ['conversations'] })

      // Show notification
      toast.success('You were added to a group chat', {
        action: {
          label: 'View',
          onClick: () => setSelectedConversationId(conversationId),
        },
      })
    })

    // Clean up event listeners
    return () => {
      socket.off('newConversation')
      socket.off('newMessage')
      socket.off('groupCreated')
      socket.off('participantAdded')
      socket.off('participantRemoved')
      socket.off('participantLeft')
      socket.off('participantKicked')
      socket.off('inviteLinkGenerated')
      socket.off('groupJoinedViaInvite')
      socket.off('addedToGroup')
    }
  }, [socket, selectedConversationId, queryClient, currentUser])

  // React Query for conversations
  const { data: conversations = [] } = useQuery({
    queryKey: ['conversations'],
    queryFn: () => api.getConversations(),
    enabled: !!currentUser,
    select: (data) => {
      // Sort conversations by updatedAt or last message time
      const sortedConversations = [...data].sort(
        (a: Conversation, b: Conversation) => {
          const timeA = new Date(a.updatedAt).getTime()
          const timeB = new Date(b.updatedAt).getTime()

          // Also check last message time as fallback or if newer
          const lastMsgTimeA = a.messages && a.messages.length > 0
            ? new Date(a.messages[a.messages.length - 1].createdAt).getTime()
            : 0
          const lastMsgTimeB = b.messages && b.messages.length > 0
            ? new Date(b.messages[b.messages.length - 1].createdAt).getTime()
            : 0

          const effectiveTimeA = Math.max(timeA, lastMsgTimeA)
          const effectiveTimeB = Math.max(timeB, lastMsgTimeB)

          return effectiveTimeB - effectiveTimeA
        }
      )

      // Sort messages within each conversation by createdAt and handle unread counts
      return sortedConversations.map((conv: Conversation) => {
        // If this is the selected conversation, force unread count to 0
        // This prevents "sticky" notifications when refetching from server
        // before the server has processed the read status
        const isSelected = conv.id === selectedConversationId

        // If selected, mark all messages as read and force unreadCount to 0
        // This ensures the UI shows no unread badge for the selected conversation
        let messages = [...conv.messages]
        let unreadCount = conv.unreadCount || 0

        if (isSelected) {
          // Mark all messages as read for selected conversation
          messages = messages.map(msg => ({ ...msg, isRead: true }))
          unreadCount = 0
        } else {
          // For non-selected conversations, use server's unreadCount
          // But also ensure messages match the unreadCount
          // If server says unreadCount is 0, mark all messages as read
          if (unreadCount === 0) {
            messages = messages.map(msg => ({ ...msg, isRead: true }))
          }
        }

        return {
          ...conv,
          unreadCount: unreadCount,
          messages: messages.sort(
            (a: Message, b: Message) =>
              new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
          ),
        }
      })
    },
    // More frequent updates for all users to ensure they see new messages
    refetchInterval: 10000,
    // Shorter stale time for all users to ensure fresher data
    staleTime: 5000,
    // Always refetch on window focus for all users
    refetchOnWindowFocus: true,
  })

  // React Query for selected conversation - optimize for faster loading
  const { data: selectedConversation } = useQuery({
    queryKey: ['conversation', selectedConversationId],
    queryFn: () =>
      selectedConversationId
        ? api.getConversation({ conversationId: selectedConversationId })
        : null,
    enabled: !!selectedConversationId && !!currentUser,
    select: (data) => {
      if (!data) return null

      // Sort messages by createdAt
      return {
        ...data,
        messages: [...data.messages].sort(
          (a: Message, b: Message) =>
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        ),
      }
    },
    refetchInterval: 5000, // More frequent updates for faster message delivery
    staleTime: 2000, // Very short stale time to ensure fresh data
    gcTime: Infinity, // Keep data in cache longer
    refetchOnWindowFocus: true, // Refetch on window focus for all users
  })

  // React Query for search results
  const { data: searchResults = [], refetch: refetchSearch } = useQuery({
    queryKey: ['searchUsers', searchTerm],
    queryFn: () => {
      if (!currentUser || !searchTerm.trim()) return []
      return api.searchUsers({
        term: searchTerm.trim(),
        userRole: currentUser.role,
      })
    },
    enabled: false, // Don't run automatically, we'll trigger it manually
  })

  // Mutation for marking messages as read

  // Mutation for creating a new conversation
  const createConversationMutation = useMutation({
    mutationFn: api.createConversation,
    onSuccess: (data) => {
      // Set the new conversation as selected
      setSelectedConversationId(data.id)

      // Invalidate conversations query to refresh the list
      queryClient.invalidateQueries({ queryKey: ['conversations'] })

      // Exit search mode
      setIsSearching(false)
      setSearchTerm('')
    },
  })

  // Mutation for creating a teacher-student chat
  const createTeacherStudentChatMutation = useMutation({
    mutationFn: api.createTeacherStudentChat,
    onSuccess: (data) => {
      // Set the new conversation as selected
      setSelectedConversationId(data.id)

      // Invalidate conversations query to refresh the list
      queryClient.invalidateQueries({ queryKey: ['conversations'] })

      // Exit search mode
      setIsSearching(false)
      setSearchTerm('')
    },
  })

  // Add a function to scroll to the bottom of messages
  const scrollToBottom = useCallback(() => {
    // Use a smaller timeout to make scrolling feel more immediate
    setTimeout(() => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({
          behavior: 'auto', // Changed from 'smooth' to 'auto' for faster scrolling
          block: 'end',
          inline: 'nearest',
        })
      }
    }, 10) // Reduced from 50ms to 10ms for faster response
  }, [messagesEndRef])

  // Scroll to bottom whenever selected conversation messages change
  useEffect(() => {
    if (selectedConversation) {
      scrollToBottom()
    }
  }, [selectedConversation?.messages, scrollToBottom])

  // Set up socket event listeners for real-time updates
  useEffect(() => {
    if (!socket) return

    // Handle new message event - optimized for speed and improved for all user roles
    socket.on('newMessage', async (data: any) => {
      // Handle different message formats
      const message = data.message || data
      const conversationId = message.conversationId

      if (!message || !conversationId) {
        console.warn('Received invalid message format:', data)
        return
      }

  // console.logReceived new message:', message)

      // STEP 1: Update the conversation in the conversations list
      queryClient.setQueryData(['conversations'], (oldData: any) => {
        if (!oldData) return oldData

        // Find the conversation
        const conversationIndex = oldData.findIndex(
          (c: Conversation) => c.id === conversationId
        )

        // If conversation not found, we need to refresh the conversations list
        if (conversationIndex === -1) {
          // Trigger a refresh of conversations
          setTimeout(() => {
            queryClient.invalidateQueries({ queryKey: ['conversations'] })
          }, 100)
          return oldData
        }

        // Check if message already exists
        const messageExists = oldData[conversationIndex].messages?.some(
          (msg: Message) => msg.id === message.id
        )

        if (messageExists) return oldData

        // Create a new array with the updated conversation
        const newData = [...oldData]

        // Determine if this is a message from another user and if the conversation is not selected
        const isFromOtherUser = message.senderId !== currentUser?.id
        const isConversationSelected = selectedConversationId === conversationId

        // If the message is from another user and the conversation is not selected, increment unread count
        // If the conversation IS selected, force unread count to 0
        let unreadCount = newData[conversationIndex].unreadCount || 0

        if (isConversationSelected) {
          unreadCount = 0
        } else if (isFromOtherUser) {
          unreadCount += 1
        }

        // Add the message to the conversation's messages array
        if (!newData[conversationIndex].messages) {
          newData[conversationIndex].messages = []
        }

        // Add the message (we already checked for duplicates above)
        newData[conversationIndex].messages.push({
          ...message,
          isRead:
            isConversationSelected || message.senderId === currentUser?.id,
        })

        // Sort messages by createdAt (oldest first)
        newData[conversationIndex].messages.sort(
          (a: Message, b: Message) =>
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        )

        // If selected, we must ensure ALL messages are marked as read to clear the badge
        // This is critical because the UI calculates the badge from the messages array
        if (isConversationSelected) {
          newData[conversationIndex].messages = newData[conversationIndex].messages.map(
            (msg: Message) => ({ ...msg, isRead: true })
          )
        }

        // Mark the message as read if the conversation is currently selected
        const updatedMessage = {
          ...message,
          isRead:
            isConversationSelected || message.senderId === currentUser?.id,
        }

        newData[conversationIndex] = {
          ...newData[conversationIndex],
          lastMessage: updatedMessage,
          updatedAt: message.createdAt,
          unreadCount: unreadCount,
        }

        // Sort conversations by updatedAt or last message time
        return newData.sort(
          (a: Conversation, b: Conversation) => {
            const timeA = new Date(a.updatedAt).getTime()
            const timeB = new Date(b.updatedAt).getTime()

            // Also check last message time as fallback or if newer
            const lastMsgTimeA = a.messages && a.messages.length > 0
              ? new Date(a.messages[a.messages.length - 1].createdAt).getTime()
              : 0
            const lastMsgTimeB = b.messages && b.messages.length > 0
              ? new Date(b.messages[b.messages.length - 1].createdAt).getTime()
              : 0

            const effectiveTimeA = Math.max(timeA, lastMsgTimeA)
            const effectiveTimeB = Math.max(timeB, lastMsgTimeB)

            return effectiveTimeB - effectiveTimeA
          }
        )
      })

      // STEP 2: If this is for the selected conversation, update it
      if (selectedConversationId === conversationId) {
        queryClient.setQueryData(
          ['conversation', selectedConversationId],
          (oldData: any) => {
            if (!oldData) return oldData

            // Check if message already exists or is a temporary message with the same content
            const existingMsgIndex = oldData.messages.findIndex(
              (msg: Message) =>
                msg.id === message.id ||
                (msg.temporary &&
                  msg.content === message.content &&
                  msg.senderId === message.senderId)
            )

            let updatedMessages

            if (existingMsgIndex >= 0) {
              // Replace the temporary message with the real one
              updatedMessages = [...oldData.messages]
              updatedMessages[existingMsgIndex] = {
                ...message,
                isRead: true, // Mark as read since we're viewing this conversation
              }
            } else {
              // Add as a new message and mark as read since we're viewing this conversation
              updatedMessages = [
                ...oldData.messages,
                {
                  ...message,
                  isRead: true,
                },
              ]
            }

            // Sort messages by timestamp
            updatedMessages.sort(
              (a: Message, b: Message) =>
                new Date(a.createdAt).getTime() -
                new Date(b.createdAt).getTime()
            )

            return {
              ...oldData,
              messages: updatedMessages,
              updatedAt: message.createdAt,
              unreadCount: 0, // Reset unread count since we're viewing this conversation
            }
          }
        )

        // Automatically mark messages as read on the server when we receive them in the selected conversation
        if (message.senderId !== currentUser?.id) {
          // Mark as read on server and invalidate queries to sync state
          api.markMessagesAsRead({ conversationId })
            .then(() => {
              // Invalidate conversations query to refetch with updated read status
              queryClient.invalidateQueries({ queryKey: ['conversations'] })
            })
            .catch(() => {
              // console.error('Error marking messages as read:', error)
            })
        }

        // Scroll to bottom immediately
        scrollToBottom()
      } else {
        // STEP 3: If this is a new message for a non-selected conversation,
        // we need to make sure the conversation data is loaded in the cache
        const cachedData = queryClient.getQueryData([
          'conversation',
          conversationId,
        ])

        // If we don't have the data or it's stale, fetch it
        if (!cachedData) {
          // Fetch the conversation data in the background
          api
            .getConversation({ conversationId })
            .then((data) => {
              // Update the cache with the fetched data
              queryClient.setQueryData(['conversation', conversationId], data)

              // If this is a new message and no conversation is selected,
              // automatically select this conversation
              if (
                !selectedConversationId &&
                message.senderId !== currentUser?.id
              ) {
                setSelectedConversationId(conversationId)
              }
            })
            .catch(() => {
              // console.error('Error fetching conversation data:', error)
            })
        } else {
          // If we have the data and this is a new message from another user,
          // consider selecting this conversation
          if (message.senderId !== currentUser?.id) {
            // If no conversation is selected, select this one
            if (!selectedConversationId) {
              setSelectedConversationId(conversationId)
            }
            // If the user hasn't interacted with the current conversation recently,
            // consider switching to this new conversation with a notification
            else {
              // Show a notification about the new message
              toast.info(
                `New message from ${message.sender?.firstname || 'someone'}`,
                {
                  action: {
                    label: 'View',
                    onClick: () => setSelectedConversationId(conversationId),
                  },
                }
              )
            }
          }
        }
      }
    })

    // Handle message received event - same handler for consistency
    socket.on('messageReceived', (data: any) => {
      // Reuse the same handler for consistency
      socket.emit('newMessage', data)
    })

    // Handle errors with better error messages (only for runtime errors, not connection errors)
    // Connection errors are handled in the socket creation useEffect above
    socket.on('error', (error: any) => {
      // Only handle runtime errors, not connection/auth errors
      // AUTH_REQUIRED errors are suppressed as they're handled during connection
      if (error.code === 'AUTH_REQUIRED') {
        console.warn('Socket auth error (suppressed):', error.message)
        return
      }

      // console.error('Socket runtime error:', error)
      const errorMessage =
        typeof error === 'string' ? error : error.message || 'Connection error'

      if (errorMessage.includes('not a participant')) {
        toast.error('You are not a participant in this conversation')
      } else {
        // Only show error if it's not a connection issue
        toast.error(errorMessage)
      }
    })

    // Clean up event listeners
    return () => {
      socket.off('newMessage')
      socket.off('messageReceived')
      socket.off('error')
    }
  }, [
    socket,
    selectedConversationId,
    queryClient,
    scrollToBottom,
    currentUser,
  ])

  // Handle search input effect
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }

    if (searchTerm && isSearching) {
      setIsSearchLoading(true)
      searchTimeoutRef.current = setTimeout(() => {
        refetchSearch().finally(() => setIsSearchLoading(false))
      }, 300)
    } else if (!searchTerm) {
      setIsSearchLoading(false)
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [searchTerm, isSearching, refetchSearch])

  // Toggle search
  const toggleSearch = () => {
    setIsSearching(!isSearching)
    if (!isSearching && searchTerm) {
      refetchSearch()
    }
  }

  // Handle sending a file message
  const handleSendFileMessage = useCallback(
    async (fileMessage: any) => {
      if (!selectedConversationId || !currentUser) {
        return
      }

      try {
        // Ensure fileMessage has all required properties
        if (!fileMessage || !fileMessage.id || !fileMessage.createdAt) {
          // console.error('Invalid file message received:', fileMessage)
          toast.error('Invalid file message format')
          return
        }

        // Ensure file message has required file properties for file/image messages
        if (fileMessage.messageType === 'file' || fileMessage.messageType === 'image') {
          if (!fileMessage.fileName || !fileMessage.filePath || !fileMessage.mimeType) {
            // console.error('File message missing required properties:', fileMessage)
            // Try to continue anyway, but log the issue
            console.warn('File message may not display correctly due to missing properties')
          }
        }

        // STEP 1: Optimistically update the selected conversation immediately
        queryClient.setQueryData(
          ['conversation', selectedConversationId],
          (oldData: any) => {
            if (!oldData) {
              // If conversation not in cache, refetch it
              queryClient.invalidateQueries({ queryKey: ['conversation', selectedConversationId] })
              return null
            }

            // Ensure messages array exists (important for group chats)
            const existingMessages = oldData.messages || []
            
            // Check if message already exists to avoid duplicates
            const messageExists = existingMessages.some((msg: Message) => msg.id === fileMessage.id)
            if (messageExists) {
              return oldData
            }

            // Add the file message to the conversation
            const updatedMessages = [...existingMessages, fileMessage].sort(
              (a: Message, b: Message) =>
                new Date(a.createdAt).getTime() -
                new Date(b.createdAt).getTime()
            )

            return {
              ...oldData,
              messages: updatedMessages,
              updatedAt: fileMessage.createdAt,
            }
          }
        )

        // STEP 2: Optimistically update the conversations list
        queryClient.setQueryData(['conversations'], (oldData: any) => {
          if (!oldData || !Array.isArray(oldData)) return []

          // Use current timestamp to ensure this conversation is at the top
          const currentTimestamp = new Date().toISOString()

          // Find and update the conversation in the list
          const updatedConversations = oldData.map((conv: Conversation) => {
            if (conv.id === selectedConversationId) {
              // Ensure messages array exists (important for group chats)
              const existingMessages = conv.messages || []
              
              // Check if message already exists to avoid duplicates
              const messageExists = existingMessages.some((msg: Message) => msg.id === fileMessage.id)
              if (messageExists) {
                // Just update the timestamp
                return {
                  ...conv,
                  updatedAt: currentTimestamp,
                }
              }

              // Add the file message to the messages array
              const updatedMessages = [...existingMessages, fileMessage].sort(
                (a: Message, b: Message) =>
                  new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
              )

              return {
                ...conv,
                messages: updatedMessages,
                lastMessage: fileMessage,
                updatedAt: currentTimestamp, // Use current timestamp to ensure it's newest
              }
            }
            return conv
          })

          // Sort conversations by updatedAt (newest first)
          // Create a new array to ensure React Query detects the change
          const sorted = [...updatedConversations].sort(
            (a: Conversation, b: Conversation) => {
              const timeA = new Date(a.updatedAt).getTime()
              const timeB = new Date(b.updatedAt).getTime()

              // Also check last message time as fallback or if newer
              const lastMsgTimeA = a.messages && a.messages.length > 0
                ? new Date(a.messages[a.messages.length - 1].createdAt).getTime()
                : 0
              const lastMsgTimeB = b.messages && b.messages.length > 0
                ? new Date(b.messages[b.messages.length - 1].createdAt).getTime()
                : 0

              const effectiveTimeA = Math.max(timeA, lastMsgTimeA)
              const effectiveTimeB = Math.max(timeB, lastMsgTimeB)

              return effectiveTimeB - effectiveTimeA
            }
          )

          return sorted
        })

        // STEP 3: Scroll to bottom immediately after optimistic update
        scrollToBottom()

        // File message is already sent via the ChatFileUpload component
        // so we don't need to send it again here
      } catch (error: any) {
        // console.error('Error handling file message:', error)
        toast.error('Failed to display file message')
      }
    },
    [selectedConversationId, currentUser, queryClient, scrollToBottom]
  )

  // Handle sending a message with ultra-fast optimistic updates
  const handleSendMessage = useCallback(
    async (e?: React.FormEvent) => {
      e?.preventDefault()

      if (
        !message.trim() ||
        !selectedConversationId ||
        !currentUser
      ) {
        return
      }

      try {
        // Store message content and clear input field immediately
        const messageContent = message.trim()
        setMessage('')

        // Create a temporary message with a unique ID
        const tempId = `temp-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`
        const tempMessage: Message = {
          id: tempId,
          content: messageContent,
          senderId: currentUser.id,
          conversationId: selectedConversationId,
          createdAt: new Date().toISOString(),
          isRead: false,
          temporary: true,
          sender: currentUser, // Include sender info for better display
        }

        // STEP 1: Optimistically update the selected conversation immediately
        queryClient.setQueryData(
          ['conversation', selectedConversationId],
          (oldData: any) => {
            if (!oldData) return null

            // Use current timestamp to ensure this conversation is at the top
            const currentTimestamp = new Date().toISOString()

            // Add the temporary message to the conversation
            const updatedMessages = [...oldData.messages, tempMessage].sort(
              (a: Message, b: Message) =>
                new Date(a.createdAt).getTime() -
                new Date(b.createdAt).getTime()
            )

            return {
              ...oldData,
              messages: updatedMessages,
              updatedAt: currentTimestamp, // Use current timestamp to ensure it's newest
            }
          }
        )

        // STEP 2: Optimistically update the conversations list
        queryClient.setQueryData(['conversations'], (oldData: any) => {
          if (!oldData) return []

          // Use current timestamp to ensure this conversation is at the top
          const currentTimestamp = new Date().toISOString()

          // Find and update the conversation in the list
          const updatedConversations = oldData.map((conv: Conversation) => {
            if (conv.id === selectedConversationId) {
              // Add the temp message to the messages array
              const updatedMessages = [...(conv.messages || []), tempMessage].sort(
                (a: Message, b: Message) =>
                  new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
              )

              return {
                ...conv,
                messages: updatedMessages,
                lastMessage: tempMessage,
                updatedAt: currentTimestamp, // Use current timestamp to ensure it's newest
              }
            }
            return conv
          })

          // Sort conversations by updatedAt or last message time (newest first)
          // Create a new array to ensure React Query detects the change
          const sorted = [...updatedConversations].sort(
            (a: Conversation, b: Conversation) => {
              const timeA = new Date(a.updatedAt).getTime()
              const timeB = new Date(b.updatedAt).getTime()

              // Also check last message time as fallback or if newer
              const lastMsgTimeA = a.messages && a.messages.length > 0
                ? new Date(a.messages[a.messages.length - 1].createdAt).getTime()
                : 0
              const lastMsgTimeB = b.messages && b.messages.length > 0
                ? new Date(b.messages[b.messages.length - 1].createdAt).getTime()
                : 0

              const effectiveTimeA = Math.max(timeA, lastMsgTimeA)
              const effectiveTimeB = Math.max(timeB, lastMsgTimeB)

              return effectiveTimeB - effectiveTimeA
            }
          )

          return sorted
        })

        // STEP 3: Scroll to bottom immediately after optimistic update
        scrollToBottom()

        // STEP 4: Send the message to the server in the background
        // Only send the fields expected by SendMessageDto
        const serverMessage = await api.sendMessage({
          content: messageContent,
          conversationId: selectedConversationId,
        })

        // Update the selected conversation with the real message
        queryClient.setQueryData(
          ['conversation', selectedConversationId],
          (oldData: any) => {
            if (!oldData) return null

            // Replace the temporary message with the real one
            // Find and remove any temporary messages with matching content
            const updatedMessages = oldData.messages
              .filter(
                (msg: Message) =>
                  !(msg.temporary && msg.content === messageContent)
              )
              .concat(serverMessage)
              .sort(
                (a: Message, b: Message) =>
                  new Date(a.createdAt).getTime() -
                  new Date(b.createdAt).getTime()
              )

            // Use current timestamp to ensure this conversation is at the top
            const currentTimestamp = new Date().toISOString()

            return {
              ...oldData,
              messages: updatedMessages,
              updatedAt: currentTimestamp, // Use current timestamp to ensure it's newest
            }
          }
        )

        // Update the conversations list with the real message
        queryClient.setQueryData(['conversations'], (oldData: any) => {
          if (!oldData) return []

          // Use current timestamp to ensure this conversation is at the top
          const currentTimestamp = new Date().toISOString()

          // Find and update the conversation in the list
          const updatedConversations = oldData.map((conv: Conversation) => {
            if (conv.id === selectedConversationId) {
              // Add the new message to the messages array if it's not already there
              const updatedMessages = [...(conv.messages || [])]

              // Check if message already exists (to avoid duplicates)
              const exists = updatedMessages.some(m => m.id === serverMessage.id || (m.temporary && m.content === serverMessage.content))

              if (!exists) {
                updatedMessages.push(serverMessage)
              } else {
                // Replace temporary message with real one
                const index = updatedMessages.findIndex(m => m.temporary && m.content === serverMessage.content)
                if (index !== -1) {
                  updatedMessages[index] = serverMessage
                }
              }

              // Sort messages by createdAt (oldest first)
              const sortedMessages = updatedMessages.sort(
                (a: Message, b: Message) =>
                  new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
              )

              return {
                ...conv,
                messages: sortedMessages,
                lastMessage: serverMessage,
                updatedAt: currentTimestamp, // Use current timestamp to ensure it's newest
              }
            }
            return conv
          })

          // Sort conversations by updatedAt or last message time (newest first)
          // Create a new array to ensure React Query detects the change
          const sorted = [...updatedConversations].sort(
            (a: Conversation, b: Conversation) => {
              const timeA = new Date(a.updatedAt).getTime()
              const timeB = new Date(b.updatedAt).getTime()

              // Also check last message time as fallback or if newer
              const lastMsgTimeA = a.messages && a.messages.length > 0
                ? new Date(a.messages[a.messages.length - 1].createdAt).getTime()
                : 0
              const lastMsgTimeB = b.messages && b.messages.length > 0
                ? new Date(b.messages[b.messages.length - 1].createdAt).getTime()
                : 0

              const effectiveTimeA = Math.max(timeA, lastMsgTimeA)
              const effectiveTimeB = Math.max(timeB, lastMsgTimeB)

              return effectiveTimeB - effectiveTimeA
            }
          )

          return sorted
        })

        // Scroll to bottom again after server update
        scrollToBottom()
      } catch (error: any) {
        // console.error('Error sending message:', error)

        // Mark the temporary message as error
        queryClient.setQueryData(
          ['conversation', selectedConversationId],
          (oldData: any) => {
            if (!oldData) return null

            // Find the temporary message and mark it as error
            const updatedMessages = oldData.messages.map((msg: Message) => {
              if (msg.temporary) {
                return { ...msg, error: true }
              }
              return msg
            })

            return {
              ...oldData,
              messages: updatedMessages,
            }
          }
        )

        // Show error toast with specific message
        if (
          error.response?.status === 403 ||
          (error.message && error.message.includes('not a participant'))
        ) {
          toast.error('You are not a participant in this conversation')
        } else {
          toast.error(
            'Failed to send message: ' +
            (error.response?.data?.message ||
              error.message ||
              'Unknown error')
          )
        }
      }
    },
    [
      currentUser,
      message,
      selectedConversationId,
      queryClient,
      scrollToBottom,
    ]
  )

  // Start new conversation
  const startNewConversation = useCallback(
    async (userId: string) => {
      if (!currentUser || !socket) return

      const selectedUser = searchResults.find(
        (user: User) => user.id === userId
      )
      if (!selectedUser) {
        // console.error('Selected user not found in search results')
        return
      }

      // Use the teacher-student specific endpoint if the current user is a teacher
      // and the selected user is a student
      if (currentUser.role === 'Teacher' && selectedUser.role === 'Student') {
        createTeacherStudentChatMutation.mutate(
          {
            studentId: userId,
            initialMessage: 'Hello,',
          },
          {
            onSuccess: (data) => {
              // Join the conversation room via socket
              socket.emit('joinConversation', {
                conversationId: data.id,
              })
            },
            onError: () => {
              // console.error('Error creating teacher-student chat:', error)
              toast.error('Failed to create conversation')
            },
          }
        )
      } else {
        // Regular conversation creation
        createConversationMutation.mutate(
          {
            participantIds: [userId, currentUser.id],
            isGroupChat: false,
            name: `${currentUser.firstname} ${currentUser.lastname} - ${selectedUser.firstname} ${selectedUser.lastname}`,
          },
          {
            onSuccess: (data) => {
              // Join the conversation room via socket
              socket.emit('joinConversation', {
                conversationId: data.id,
              })
            },
            onError: () => {
              // console.error('Error creating conversation:', error)
              toast.error('Failed to create conversation')
            },
          }
        )
      }
    },
    [
      currentUser,
      socket,
      searchResults,
      createTeacherStudentChatMutation,
      createConversationMutation,
    ]
  )

  // Handle selecting a conversation
  const handleSelectConversation = useCallback(
    async (conversationId: string) => {
      if (!conversationId) return

      // Immediately set the selected conversation ID for UI responsiveness
      setSelectedConversationId(conversationId)

      // Immediately scroll to bottom for better UX
      scrollToBottom()

      try {
        // Fetch the latest conversation data from the server
        const data = await api.getConversation({ conversationId })

        if (!data) {
          throw new Error('Failed to fetch conversation')
        }

        // Ensure messages are sorted by timestamp
        const sortedMessages = [...(data.messages || [])].sort(
          (a: Message, b: Message) =>
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        )

        // Update the conversation with the latest data
        const updatedConversation = {
          ...data,
          messages: sortedMessages,
        }

        // Update the cache with the latest data
        queryClient.setQueryData(
          ['conversation', conversationId],
          updatedConversation
        )

        // Scroll to bottom after updating with new data
        scrollToBottom()

        // Always mark conversation as read when selecting it - both in the backend and locally
        // This ensures unread counts are cleared and stay cleared even after refresh
        try {
          // Update on the server first - always mark as read when viewing
          await api.markMessagesAsRead({ conversationId })
          
          // Update locally for immediate UI feedback
          queryClient.setQueryData(
            ['conversation', conversationId],
            (oldData: any) => {
              if (!oldData) return oldData

              // Mark ALL messages as read (both from user and others)
              // This ensures consistency with server state
              const updatedMessages = oldData.messages.map((msg: Message) => ({
                ...msg,
                isRead: true,
              }))

              return {
                ...oldData,
                messages: updatedMessages,
                unreadCount: 0, // Force to 0 to clear badge immediately
              }
            }
          )

          // Update the conversations list to reflect read status
          queryClient.setQueryData(['conversations'], (oldData: any) => {
            if (!oldData) return oldData

            return oldData.map((conv: Conversation) => {
              if (conv.id === conversationId) {
                // Mark ALL messages as read (both from user and others)
                // This ensures consistency with server state
                const updatedMessages = conv.messages.map((msg: Message) => ({
                  ...msg,
                  isRead: true,
                }))

                return {
                  ...conv,
                  messages: updatedMessages,
                  unreadCount: 0, // Force to 0 to clear badge immediately
                }
              }
              return conv
            })
          })
          
          // Invalidate queries after a short delay to allow server to process
          // This ensures the next refetch gets the updated read status from server
          setTimeout(() => {
            queryClient.invalidateQueries({ queryKey: ['conversations'] })
            queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] })
          }, 500)
        } catch (readError) {
          // console.error('Failed to mark messages as read:', readError)
          // Still update locally for better UX even if server update fails
          queryClient.setQueryData(
            ['conversation', conversationId],
            (oldData: any) => {
              if (!oldData) return oldData
              return {
                ...oldData,
                unreadCount: 0,
                messages: oldData.messages.map((msg: Message) => ({
                  ...msg,
                  isRead: msg.senderId === currentUser?.id ? msg.isRead : true,
                })),
              }
            }
          )
        }
      } catch (error: any) {
        // console.error('Error selecting conversation:', error)

        // Handle permission errors specifically
        if (
          error.response?.status === 403 ||
          (error.message && error.message.includes('not a participant'))
        ) {
          toast.error('You are not a participant in this conversation')

          // Remove this conversation from the selected conversation
          setSelectedConversationId(null)

          // Refresh conversations list to get accurate data
          queryClient.invalidateQueries({ queryKey: ['conversations'] })
        } else {
          // Handle other errors
          // toast.error(
          //   'Failed to load conversation: ' +
          //     (error.response?.data?.message ||
          //       error.message ||
          //       'Unknown error')
          // )
        }
      }
    },
    [queryClient, scrollToBottom, currentUser]
  )

  // Get the other participant in a conversation
  const getOtherParticipant = useCallback(
    (conversation: Conversation): User | undefined => {
      if (!currentUser || !conversation.participants) return undefined
      return conversation.participants.find((p) => p.id !== currentUser.id)
    },
    [currentUser]
  )

  // Filter conversations based on the conversation filter
  const filteredConversations = conversations.filter((conversation) => {
    if (!conversationFilter) return true

    const otherUser = getOtherParticipant(conversation)
    if (!otherUser) return false

    const fullName =
      `${otherUser.firstname} ${otherUser.lastname}`.toLowerCase()
    return fullName.includes(conversationFilter.toLowerCase())
  })

  return {
    conversations: filteredConversations,
    selectedConversation,
    selectedConversationId,
    message,
    setMessage,
    isLoading,
    searchTerm,
    setSearchTerm,
    searchResults,
    isSearching,
    isSearchLoading,
    messagesEndRef,
    handleSendMessage,
    handleSendFileMessage,
    startNewConversation,
    handleSelectConversation,
    getOtherParticipant,
    toggleSearch,
    conversationFilter,
    setConversationFilter,
    searchUsers: api.searchStudents, // Export searchStudents for group chat
  }
}
